import { SeededRandom } from './SeededRandom';
import { TileColor, TileSymbol, ALL_COLORS, ALL_SYMBOLS, TileType, PlaceableTile, tilesMatch } from '../config/tiles';
import { ShapeId, getShape } from '../config/shapes';

const DAILY_COLORS: TileColor[] = ALL_COLORS.slice(0, 4);
const DAILY_SYMBOLS: TileSymbol[] = ALL_SYMBOLS.slice(0, 4);
const ALL_SHAPES: ShapeId[] = ['square', 'diamond', 'rectangle', 'hourglass', 'heart'];
const PRE_POPULATE_COUNT = 5;

export interface DailyPuzzle {
  shapeId: ShapeId;
  colors: TileColor[];
  symbols: TileSymbol[];
  prePopulated: Array<{ row: number; col: number; tile: TileType }>;
}

export function generateDailyPuzzle(date: string): DailyPuzzle {
  const rng = new SeededRandom(`daily-puzzle-${date}`);

  const shapeId = ALL_SHAPES[rng.nextInt(ALL_SHAPES.length)];
  const shape = getShape(shapeId);
  const size = shape.size;

  const activeCells: Array<{ row: number; col: number }> = [];
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (shape.mask[row][col]) {
        activeCells.push({ row, col });
      }
    }
  }

  const prePopulated = placePrePopulatedTiles(rng, activeCells);

  return {
    shapeId,
    colors: DAILY_COLORS,
    symbols: DAILY_SYMBOLS,
    prePopulated,
  };
}

export function createDailyRng(date: string): () => number {
  const rng = new SeededRandom(`daily-queue-${date}`);
  return () => rng.next();
}

function placePrePopulatedTiles(
  rng: SeededRandom,
  activeCells: Array<{ row: number; col: number }>,
): Array<{ row: number; col: number; tile: TileType }> {
  const placed: Array<{ row: number; col: number; tile: TileType }> = [];
  const occupied = new Set<string>();

  const candidates = rng.shuffle(activeCells);

  for (const pos of candidates) {
    if (placed.length >= PRE_POPULATE_COUNT) break;

    const key = `${pos.row},${pos.col}`;
    if (occupied.has(key)) continue;

    if (placed.length === 0) {
      const tile = randomTile(rng);
      placed.push({ ...pos, tile });
      occupied.add(key);
      continue;
    }

    const neighbors = getNeighborTiles(pos.row, pos.col, placed);
    if (neighbors.length === 0) continue;

    const tile = generateMatchingTile(rng, neighbors);
    placed.push({ ...pos, tile });
    occupied.add(key);
  }

  return placed;
}

function getNeighborTiles(
  row: number,
  col: number,
  placed: Array<{ row: number; col: number; tile: TileType }>,
): TileType[] {
  const offsets = [[-1, 0], [1, 0], [0, -1], [0, 1]];
  const neighbors: TileType[] = [];

  for (const [dr, dc] of offsets) {
    const nr = row + dr;
    const nc = col + dc;
    const found = placed.find(p => p.row === nr && p.col === nc);
    if (found) neighbors.push(found.tile);
  }

  return neighbors;
}

function generateMatchingTile(rng: SeededRandom, neighbors: TileType[]): TileType {
  for (let attempt = 0; attempt < 50; attempt++) {
    const tile = randomTile(rng);
    const matchesAll = neighbors.every(n => tilesMatch(tile, n));
    if (matchesAll) return tile;
  }

  const neighbor = neighbors[0];
  if (rng.next() < 0.5) {
    return { color: neighbor.color, symbol: rng.pick(DAILY_SYMBOLS) };
  }
  return { color: rng.pick(DAILY_COLORS), symbol: neighbor.symbol };
}

function randomTile(rng: SeededRandom): TileType {
  return {
    color: rng.pick(DAILY_COLORS),
    symbol: rng.pick(DAILY_SYMBOLS),
  };
}

export function buildDailyCells(
  puzzle: DailyPuzzle,
): Array<{ tile: PlaceableTile | null; restored: boolean }> {
  const shape = getShape(puzzle.shapeId);
  const size = shape.size;
  const cells: Array<{ tile: PlaceableTile | null; restored: boolean }> = [];

  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      const prePlaced = puzzle.prePopulated.find(p => p.row === row && p.col === col);
      cells.push({
        tile: prePlaced ? prePlaced.tile : null,
        restored: !!prePlaced,
      });
    }
  }

  return cells;
}
